import PropTypes from 'prop-types';

import { Badge } from '@vimeo/iris/components';
import { Tabs } from '@vimeo/iris/layout';
import { Header, Link, Paragraph } from '@vimeo/iris/typography';

import CodeBlock from 'src/_components/CodeBlock';
import CollapsableContent from 'src/_components/CollapsableContent';
import irisMarkdown from 'src/_components/Markdown';
import StyledNotice, { NoticeText, NoticeWrapper } from 'src/_components/StyledNotice';
import { withVersion } from 'src/_tools/withVersion';

import exampleResponseJSON from './exampleResponse.json';
import Parameters from './Parameters';
import styles from './ReferenceGroup.module.scss';
import { RESPONSE_HTTP_CODES } from './responseHttpCodes';

const Operation = function (props) {
  const {
    currentOperation = null,
    method,
    onClickClose,
    onClickOpen,
    path,
    playgroundButton,
    schemas,
    spec,
  } = props;

  const anchor = spec.operationId;

  let hideOperation = true;
  let operationPaths = [path];
  let summary = <span className={styles.summary_header}>{spec.summary}</span>;
  let allCapabilities = devsite?.user?.apps?.all_capabilities;
  let has_private_visibility =
    typeof spec['x-devsite-visibility-private'] !== 'undefined' &&
    spec['x-devsite-visibility-private'];
  let has_capability_visibility =
    typeof spec['x-devsite-vendor-tags'] !== 'undefined' && spec['x-devsite-vendor-tags'];

  if (currentOperation !== null && currentOperation === anchor) {
    hideOperation = false;
  } else if (window.location.hash === '#' + anchor) {
    hideOperation = false;
  } else {
    hideOperation = true;
  }

  if (!hideOperation) {
    if (typeof spec['x-mill-path-aliases'] !== 'undefined') {
      Object.keys(spec['x-mill-path-aliases']).map(function (alias) {
        operationPaths.push(spec['x-mill-path-aliases'][alias]);
      });
    }
  }

  //Badge display logic
  if (has_private_visibility || has_capability_visibility) {
    let private_badge = (
      <Badge href="#" format="live" size="sm" className={styles.badge}>
        Private
      </Badge>
    );

    let capabilityPrefix = '';
    if (has_capability_visibility && allCapabilities?.beta && allCapabilities?.public) {
      let operationCapabilities = spec['x-devsite-vendor-tags'].map((i) =>
        (i || '').replace('capability:', ''),
      );
      let isBeta = allCapabilities.beta.filter((c) => operationCapabilities.includes(c)).length > 0;
      let isPublic =
        allCapabilities.public.filter((c) => operationCapabilities.includes(c)).length > 0;
      if (isBeta) {
        capabilityPrefix = 'Beta';
      } else if (isPublic) {
        capabilityPrefix = 'Public';
      } else {
        capabilityPrefix = 'Internal';
      }
    }

    let capability_badge = (
      <Badge href="#" format="live" size="sm" className={styles.badge}>
        {capabilityPrefix} Capability
      </Badge>
    );

    let badge_display;
    if (has_private_visibility && has_capability_visibility) {
      badge_display = capability_badge;
    } else {
      badge_display = has_capability_visibility ? capability_badge : private_badge;
    }

    summary = (
      <span className={styles.summary_header}>
        {spec.summary}
        {badge_display}
      </span>
    );
  }

  // Format a message to display the required scope and app capabilities
  let getNotifications = (spec) => {
    const vendorTags = (spec['x-devsite-vendor-tags'] || []).map((tag) =>
      tag.replace('capability:', ''),
    );
    const scopes = typeof spec.security === 'undefined' ? [] : spec.security[0].oauth2;

    if (vendorTags.length === 0 && scopes.length === 0) {
      return;
    }

    if (vendorTags.includes('CAPABILITY_ANALYTICS_API') && !devsite?.user?.apps.has_analytics) {
      return 'This method is available to enterprise users only. To request access, reach out to your account team.';
    }

    let scopeString = '';
    if (scopes.length === 1) {
      scopeString = scopes[0] + ' scope';
    }
    if (scopes.length > 1) {
      scopeString = scopes.join(' and ') + ' scopes';
    }

    let capabilityString = '';
    if (vendorTags.length === 1) {
      capabilityString = `capability ${vendorTags[0]}`;
    }

    if (vendorTags.length > 1) {
      capabilityString = `capabilities ${vendorTags.join(' and ')}`;
    }

    if (scopeString && !capabilityString) {
      return `This method requires a token with the ${scopeString}.`;
    }

    if (capabilityString && !scopeString) {
      return `This method requires an app with the ${capabilityString}.`;
    }

    return `This method requires a token with the ${scopeString} and an app with the ${capabilityString}.`;
  };

  let exampleResponse = null;

  const getReferenceTab = () => {
    let has200Response = false;
    let linkedRef = '';
    Object.keys(spec.responses).map(function (httpCode, i) {
      let response = spec.responses[httpCode];

      // Only show the response example if we have a successful 200-ish response
      // from the resource.
      if (httpCode >= 200 && httpCode < 300 && typeof response.content !== 'undefined') {
        let schema = response.content[Object.keys(response.content)[0]];
        let schemaRef = null;
        if (typeof schema !== 'undefined') {
          schema = schema.schema;

          if (typeof schema['$ref'] !== 'undefined') {
            // Handles object responses.
            schemaRef = schema['$ref'];
          } else if (
            typeof schema['items'] !== 'undefined' &&
            typeof schema['type'] !== 'undefined' &&
            schema['type'] === 'array'
          ) {
            // Handles array<object> responses.
            schemaRef = schema['items']['$ref'];
          }

          if (schemaRef !== null) {
            schemaRef = schemaRef.replace(/#\/components\/schemas\//, '');

            if (schemas?.status !== 'success') {
              has200Response = true;
              exampleResponse = schemas?.data;
              linkedRef = schemaRef;
            } else if (typeof schemas?.data[schemaRef] !== 'undefined') {
              has200Response = true;
              exampleResponse = schemas?.data[schemaRef];
              linkedRef = schemaRef;
            } else {
              has200Response = true;
              exampleResponse = exampleResponseJSON.empty.data;
              linkedRef = schemaRef;
            }
          }
        }
      }
    });

    if (!has200Response) {
      return false;
    }

    const refLink = withVersion('/api/reference/response/' + linkedRef);
    const refName = linkedRef.replace(/-/g, ' ');

    return (
      <Tabs.Panel
        label="Reference"
        onOpen={() => {
          window.location = refLink;
        }}
      >
        <Paragraph size="2">
          See our{' '}
          <Link variant="minimal" href={refLink}>
            response documentation
          </Link>{' '}
          for descriptions of the fields in the {refName} representation.
        </Paragraph>
      </Tabs.Panel>
    );
  };

  const DetailsTab = () => {
    return (
      <Tabs.Panel label="Details">
        <table>
          <thead>
            <tr>
              <th>HTTP Status</th>
              <th>Explanation</th>
            </tr>
          </thead>
          <tbody>
            {Object.keys(spec.responses).map(function (httpCode, i) {
              let response = spec.responses[httpCode];
              return (
                <tr key={i}>
                  <td className={styles.http_code}>
                    {httpCode} {RESPONSE_HTTP_CODES[httpCode]}
                  </td>
                  <td>
                    {irisMarkdown(response.description, {
                      allowNestedParagraphs: false,
                      convertParagraphsToSpans: false,
                      unstyledLists: true,
                    })}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Tabs.Panel>
    );
  };

  return (
    <div>
      <CollapsableContent
        className={styles.resource_header}
        element={'h3'}
        indented={false}
        isActive={!hideOperation}
        id={anchor}
        href={'#' + anchor}
        label={summary}
        secondaryElement={playgroundButton}
        onClickOpen={onClickOpen}
        onClickClose={onClickClose}
      >
        {typeof spec.description !== 'undefined' && irisMarkdown(spec.description)}

        {spec.deprecated && (
          <NoticeWrapper format="negative">
            <NoticeText>
              This method is deprecated and may be removed in future versions of the API.
            </NoticeText>
          </NoticeWrapper>
        )}

        <Header size="4">Request</Header>

        <CodeBlock
          endpoint={{
            method: method,
            resources: operationPaths,
          }}
        />

        {getNotifications(spec) && <StyledNotice>{getNotifications(spec)}</StyledNotice>}

        <Parameters specification={spec} />

        <Header size="4">Response</Header>

        <div className={styles.tabs}>
          {getReferenceTab() ? (
            <Tabs>
              <DetailsTab label={'Details'} />
              <Tabs.Panel label="Example">
                <CodeBlock language="json">{exampleResponse}</CodeBlock>
              </Tabs.Panel>
              {getReferenceTab()}
            </Tabs>
          ) : (
            <DetailsTab label={'Details'} />
          )}
        </div>
      </CollapsableContent>
    </div>
  );
};

Operation.propTypes = {
  currentOperation: PropTypes.string,
  method: PropTypes.string,
  onClickClose: PropTypes.any,
  onClickOpen: PropTypes.any,
  path: PropTypes.string,
  playgroundButton: PropTypes.any,
  schemas: PropTypes.oneOfType([
    PropTypes.array, // Is supplied when an operation doesn't actually have a schema'd response.
    PropTypes.object,
  ]),
  spec: PropTypes.object,
};

export default Operation;
